RivalIQ.module('state', function (state, App, Backbone, Marionette, $, _, moment) {

    'use strict';

    const landscapeState = state.landscape = new RivalIQ.models.Landscape();

    /**
     * Converts an array of companies into CompanyData.
     * @param {object[]} companies
     * @returns {RivalIQ.collections.CompanyData}
     */
    const companiesToCompanyData = (function () {

        let companyData;

        return function (companies) {
            // RivalIQ.collections.CompanyData is undefined on the cancelled and suspended pages.
            if (!companyData && RivalIQ.collections.CompanyData) {
                companyData = new RivalIQ.collections.CompanyData();
                companyData.initializeCompareDataFromResults(companies);
                companyData.processResults(companies);
            }

            return companyData;
        };

    }());

    /**
     * Updates data dates from CompanyData.
     * @param {RivalIQ.collections.CompanyData} companyData
     * @param {boolean} [silent]
     */
    function updateDataDates(companyData, silent) {
        state.landscape.set({
            stalestSocialData: companyData.stalestSocialData,
            earliestPostData: companyData.earliestPostData,
            earliestPresenceData: companyData.earliestPresenceData
        }, {
            silent: (silent === true)
        });
    }

    state.utils.overrideStateSet(landscapeState);
    state.utils.overrideStateGet(landscapeState, {
        earliestPresenceData: moment.utc,
        earliestPostData: moment.utc,
        companies: companiesToCompanyData
    });

    landscapeState.getCompanyById = function (id) {

        const me = this;
        const companies = me.get('companies');
        return companies && companies.get(parseInt(id));
    };

    landscapeState.getFocusCompany = function () {

        const me = this;
        const focusCompanyId = state.utils.getFocusedCompanyId();
        return me.getCompanyById(focusCompanyId);
    };

    landscapeState.getCompaniesByCompanySet = function (companySetDescriptor) {

        const me = this;
        const companies = me.get('companies');
        return companies.getCompaniesByCompanySet(companySetDescriptor);
    };

    /**
     * Gets a curation model (e.g., options, settings, etc.) for a specified curation.
     * @param curation The curation to retrieve.
     * @returns {*}
     */
    landscapeState.getCuration = _.memoize(function (curation) {

        const me = this;
        const curations = me.get('curations');
        const attributes = _.get(curations, curation, {});
        const model = new Backbone.Model(attributes);

        me.listenTo(model, 'change', function () {

            const curations = me.get('curations');
            curations[curation] = model.toJSON();
            me.set({
                curations: curations
            });
        });

        return model;
    });

    landscapeState.channelHidden = function (channel) {
        const me = this;
        const props = _.extend({}, RivalIQ.state.account.attributes, RivalIQ.state.plan.attributes, me.attributes);
        return RivalIQ.props.channelHidden(props, channel);
    };

    landscapeState.channelAccess = function (channel) {
        const me = this;
        const props = _.extend({}, RivalIQ.state.account.attributes, RivalIQ.state.plan.attributes, me.attributes);
        return RivalIQ.props.channelAccess(props, channel);
    };

    landscapeState.getSourceAccessMask = function () {
        const me = this;
        const props = _.extend({}, RivalIQ.state.account.attributes, RivalIQ.state.plan.attributes, me.attributes);
        return RivalIQ.props.sourceAccessMask(props);
    };

    landscapeState.setActiveSourceMask = function (sourceMask, active) {
        const me = this;

        let activeSources = me.get('activeSources');
        if (active) {
            activeSources = activeSources | sourceMask;
        } else {
            activeSources = activeSources & (~sourceMask);
        }

        me.set('activeSources', activeSources);
    };

    landscapeState.pollForNewCompanyData = function () {
        const me = this;

        if (landscapeState.poller) {
            landscapeState.poller.disable();
        }

        landscapeState.poller = new RivalIQ.data.DataUpdatePoller({
            landscapeId: me.get('id')
        });

        landscapeState.poller.poll();
    };

    /*
     * Record that the server has new landscape data available, and set `_newDataAvailable` to true so that
     * we'll invalidate all the caches on page navigation.
     */
    landscapeState.onNewDataAvailable = function (newDataVersion) {
        const me = this;

        RivalIQ.state.landscape.set('data_version', newDataVersion);
        me._newDataAvailable = true;
    };

    landscapeState.isNewDataAvailable = function () {
        const me = this;
        return me._newDataAvailable;
    };

    landscapeState.isDirty = function () {
        const me = this;
        return Boolean(me._isDirty);
    };

    landscapeState.isUpdateComplete = function () {
        const me = this;
        return me.get('landscapeDataUpdateStatus') === RivalIQ.data.DataUpdateStates.UPDATE_COMPLETE;
    };

    landscapeState.invalidateStaleCaches = function () {
        const me = this;

        if (me.isNewDataAvailable()) {
            me.invalidateCaches(false);
        }
    };

    landscapeState.invalidateCaches = function (reissueInflightRequests) {
        const me = this;
        me.fetch();
        me._newDataAvailable = false;

        if (me.isUpdateComplete()) {
            me.set('landscapeDataUpdateStatus', RivalIQ.data.DataUpdateStates.NO_UPDATE);
        }

        RivalIQ.data.CacheLayer.invalidateAllCaches(reissueInflightRequests);
    };

    /**
     * A property map transforming settings into state, and state
     * into persistence.
     * @type {*[]}
     */
    const statePropertyMap = [
        {
            settingProperty: 'activeLandscapeId',
            stateProperty: 'id',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'numUp',
            default: 3
        },
        {
            settingProperty: 'accountId',
            stateProperty: 'account_id',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'email',
            stateProperty: 'emailSettings',
            default: {}
        },
        {
            settingProperty: 'activeBranding'
        },
        {
            settingProperty: 'brandCustomization'
        },
        {
            settingProperty: 'activeLandscapeCloneUrl',
            stateProperty: 'cloneUrl',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'activeLandscapeCompanies',
            stateProperty: 'companies',
            path: null,
            immutable: true
        },
        {
            settingProperty: 'activeLandscapeIsCompanyAuditable',
            stateProperty: 'isCompanyAuditable',
            persistenceProperty: 'is_company_auditable',
            path: null
        },
        {
            settingProperty: 'activeLandscapeAuditSettings',
            stateProperty: 'auditSettings',
            path: null,
            immutable: true,
            default: {}
        },
        {
            settingProperty: 'widgets',
            default: {}
        },
        {
            settingProperty: 'curations',
            default: {}
        },
        {
            settingProperty: 'columns',
            default: {}
        },
        {
            settingProperty: 'stalestSocialData',
            default: null,
            immutable: true
        },
        {
            settingProperty: 'earliestPostData',
            default: undefined,
            immutable: true
        },
        {
            settingProperty: 'earliestPresenceData',
            default: undefined,
            immutable: true
        },
        {
            settingProperty: 'landscapeDataUpdateStatus',
            default: null,
            immutable: true
        },
        {
            settingProperty: 'activeLandscapeActiveSources',
            path: null,
            stateProperty: 'activeSources',
            persistenceProperty: 'active_sources'
        },
        {
            settingProperty: 'activeLandscapeMetadataVersion',
            path: null,
            stateProperty: 'metadata_version',
            immutable: true
        },
        {
            settingProperty: 'activeLandscapeDataVersion',
            path: null,
            stateProperty: 'data_version',
            immutable: true
        },
        {
            settingProperty: 'timezone',
            path: null,
            persistenceProperty: 'timezone'
        },
        {
            settingProperty: 'selectedBenchmarkCompareAgainst',
            default: 'exclude_focus_company_average'
        },
        {
            settingProperty: 'activeFacebookAdAccounts',
            default: []
        },
        {
            settingProperty: 'alertEmailSchedule',
            default: {}
        },
        {
            settingProperty: 'scheduledAlertsRunInfo',
            path: null,
            persistenceProperty: 'scheduled_alerts_run_info'
        }
    ];

    function initializeLandscapeStateFromProperties(landscapeState, properties) {
        state.utils.initializeState('landscape', 'activeLandscapeSettings',
            statePropertyMap, landscapeState, properties);

        landscapeState.on('change:timezone', state.timePeriods.onLandscapeTimezoneChangeTimePeriod);
        if (RivalIQ.data.companyData) {
            const timePeriodType = state.timePeriods.types.LAST_SEVEN_DAYS;
            const attributes = {
                landscapeId: landscapeState.id,
                // TODO clean up use of tag here
                tag: RivalIQ.state.companySets.none,
                granularity: RivalIQ.enums.dataGranularity.presence,
                sources: RivalIQ.data.channels.forIdentity(true).pluck('source'),
                timePeriod: state.timePeriods.getDefinitionByType(timePeriodType),
                activeSources: landscapeState.get('activeSources')
            };

            const collection = landscapeState.get('companies');
            RivalIQ.data.companyData.set(attributes, collection);

            // TODO COMPANY_DATA
            // Make sure that we also update the dates when backing companies
            // change (add, remove, presence updates, etc.)
            updateDataDates(collection);
        }
    }

    (function () {

        let hasInitialized = false;

        landscapeState.initialize = function (properties) {

            if (hasInitialized) {

                throw 'not supported: landscape state cannot be initialized more than once.';
            }

            initializeLandscapeStateFromProperties(this, properties);

            hasInitialized = true;

        };

        RivalIQ.once('state:initialized', function () {
            if (!landscapeState.get('timezone')) {
                landscapeState.recordTimezone();
            }
        });
    }());

}, window.moment);
